import React, { FC } from 'react';
import Modal, {
  Sheets,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from '@ingka/modal';
import Button from '@ingka/button';
import Text from '@ingka/text';
import InputField from '@ingka/input-field';
import { Stack } from '@mui/material';
import { NumericFormat } from 'react-number-format';
import useTranslation from '../../hooks/useTranslation';
import Divider from '../Divider';
import { FinancialFrameSideSheetProps } from './FinancialFrameSideSheetProps';
import { SIDE_SHEET_SECTIONS } from './utils';
import { EUR } from '../../constants';
import { colourTextAndIcon1, fontWeightBold } from '@ingka/variables';

const FinancialFrameSideSheet: FC<FinancialFrameSideSheetProps> = ({
  frame,
  loading,
  onFieldChange,
  onClose,
  onUpdate,
}) => {
  const t = useTranslation();

  const sections = [...SIDE_SHEET_SECTIONS].sort((a, b) =>
    t(`financialFrames.columns.${a.group}`).localeCompare(
      t(`financialFrames.columns.${b.group}`),
    ),
  );

  const onSave = () => {
    if (!frame || frame.id === null) return;
    onUpdate(frame.id, frame);
  };

  return (
    <Modal visible={!!frame} handleCloseBtn={onClose}>
      <Sheets
        alignment={'right'}
        size={'small'}
        header={
          <ModalHeader
            floating={false}
            title={frame ? `${frame.countryName} · ${frame.fiscalYear}` : ''}
          />
        }
        footer={
          <ModalFooter>
            <Button
              fluid
              text={t('common.cancel')}
              type="secondary"
              onClick={onClose}
              disabled={loading}
              style={{ marginBottom: 0 }}
            />
            <Button
              fluid
              text={t('common.save')}
              type="primary"
              onClick={onSave}
              loading={loading}
            />
          </ModalFooter>
        }
      >
        <ModalBody>
          {frame &&
            sections.map((section, index) => (
              <React.Fragment key={section.group}>
                {index > 0 && <Divider />}
                <Text
                  style={{ fontWeight: fontWeightBold }}
                  color={colourTextAndIcon1}
                >
                  {t(`financialFrames.columns.${section.group}`)}
                </Text>
                <Stack
                  flexDirection={'row'}
                  justifyContent={'space-between'}
                  gap={2}
                  mt={2}
                  sx={{ '.input-field': { width: '100%' } }}
                >
                  <NumericFormat
                    customInput={InputField}
                    thousandSeparator
                    type="text"
                    style={{ width: '100%' }}
                    id={`${section.opex}-input`}
                    label={t('financialFrames.columns.opex')}
                    suffixLabel={`K${EUR}`}
                    value={frame[section.opex]}
                    onValueChange={({ value }) =>
                      onFieldChange(section.opex, value)
                    }
                  />
                  <NumericFormat
                    customInput={InputField}
                    thousandSeparator
                    type="text"
                    style={{ width: '100%' }}
                    id={`${section.capex}-input`}
                    label={t('financialFrames.columns.capex')}
                    suffixLabel={`K${EUR}`}
                    value={frame[section.capex]}
                    onValueChange={({ value }) =>
                      onFieldChange(section.capex, value)
                    }
                  />
                </Stack>
              </React.Fragment>
            ))}
        </ModalBody>
      </Sheets>
    </Modal>
  );
};

export default FinancialFrameSideSheet;
